import React, { Dispatch, useCallback, useEffect } from 'react'
import FaqHeader from './faq_header/FaqHeader'
import FaqBody from './faq_body/FaqBody'
import FaqFooter from './faq_footer/FaqFooter'
import { ModelCategory, ModelQuestion, ModelTheme } from '../../types/faq/faq_model'
import fetchFactory from '../../utils/fetchFactory'
import { useIntl } from 'react-intl'
import { RootState } from '../reducers'
import { FaqType } from '../reducers/faq/types'
import { saveFaq } from '../reducers/faq/actions'
import { connect } from 'react-redux'

interface Store {
	categories: ModelCategory[],
	saveFaq: (categories: ModelCategory[]) => void,
}

type FaqProps = Store

const Faq: React.FC<FaqProps> = (
	{
		categories,
		saveFaq,
	}
) => {
	//States
	const [inputSearch, setInputSearch] = React.useState('')
	const [expandCat, setExpandCat] = React.useState(false)
	const intl = useIntl()

	//Fetch data
	useEffect(() => {
		const fd = new FormData()
		fd.append('route', '/' + intl.locale + '/api/content/faq?reference=' + process.env.REACT_APP_TARGET)

		fetchFactory(
			'/api/drupalREST',
			{
				method: 'POST',
				body: fd
			}
		)
			.then(
				(response: any) => {
					saveFaq(response[0].categories)
				}
			)
	}, [categories.length, intl.locale, saveFaq])

	//Check if some keywords include text input
	const checkKeyword = (question: ModelQuestion, input: string) => {
		const keywords: Array<string> = question.keyword.split(', ')
		const inputA: Array<string> = input.split(' ').filter(Boolean)

		return keywords.some(k => inputA.some(i => k.toLowerCase().includes(i.toLowerCase())))
	}

	//Return true if question's keywords have at least one keyword including input text
	const filterQuestions = useCallback((theme: ModelTheme, input: string) => {
		return (theme.questions.filter(question => checkKeyword(question, input)))
	}, [])

	//Return true if theme has at least one question
	const filterThemes = useCallback((category: ModelCategory, input: string) => {
		let hasFilterResult: boolean = false

		const result = category.themes.map(
			(theme) => {
				const filterResult = filterQuestions(theme, input)

				if (filterResult.length > 0) {
					hasFilterResult = true

					return {
						name: theme.name,
						questions: filterResult
					}
				} else {
					return {
						name: '',
						questions: []
					}
				}
			}
		)

		if (hasFilterResult) {
			return result
		} else {
			return []
		}
	}, [filterQuestions])

	//Update FAQ body
	const updateBody = useCallback((textInput: string) => {
		if (textInput !== '') {
			return categories.map(
				(category: ModelCategory) => {
					const filterResult = filterThemes(category, textInput)
					if (filterResult.length > 0) {
						return {
							name: category.name,
							themes: filterThemes(category, textInput),
							icon: category.icon
						}
					} else {
						return {
							name: '',
							themes: []
						}
					}
				}
			)
		} else {
			return categories
		}

	}, [categories, filterThemes])

	//Update FAQ body when user write in search input
	useEffect(() => {
		if (inputSearch !== '') {
			setExpandCat(true)
		} else {
			setExpandCat(false)
		}
	}, [inputSearch, categories, updateBody])

	const isEmpty = (cat: Array<ModelCategory>) => {
		if (cat.length > 0) {
			let empty: boolean = !(cat[0].themes && cat[0].themes.length > 0)
			cat.map((c) =>
				empty = empty && !(c.themes && c.themes.length > 0)
			)
			return empty
		} else {
			return true
		}
	}

	return (
		<div id="Faq">
			<FaqHeader setInputSearch={setInputSearch} categories={updateBody(inputSearch)} isEmpty={isEmpty} />
			<FaqBody categories={updateBody(inputSearch)} expandDefault={expandCat} isEmpty={isEmpty} />
			<FaqFooter />
		</div>
	)
}

const mapStateToProps = (state: RootState) => (
	{
		categories: state.faq
	}
)

const mapDispatchToProps = (dispatch: Dispatch<FaqType>) => (
	{
		saveFaq: (categories: ModelCategory[]) => {
			dispatch(saveFaq(categories))
		}
	}
)

export default connect(mapStateToProps, mapDispatchToProps)(Faq)
